<div class="config-layout" *transloco="let t; prefix: 'assetSettings'">
  <!-- Select Account Card -->
  <div class="config-card config-card--full config-card--gap-sm">
    <h3 class="config-card__title">{{ t('common.selectAccount') }}</h3>
    <p class="config-card__description">{{ t('maintenance.description') }}</p>
    <app-account-selector (accountChanged)="onAccountChanged($event)"></app-account-selector>
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="config-empty-state">
      <div class="spinner-border text-danger" role="status">
        <span class="visually-hidden">{{ t('common.loading') }}</span>
      </div>
    </div>
  }

  <!-- Empty State -->
  @if (!selectedAccountId && !isLoading && !hasError) {
    <div class="account-empty-state">
      <div class="account-empty-state__icon">
        <img src="assets/icons/exclamation_circle.svg" [attr.alt]="t('common.selectAccountToBegin')" />
      </div>
      <h3>{{ t('common.selectAccountToBegin') }}</h3>
      <p>{{ t('maintenance.selectAccountHelp') }}</p>
    </div>
  }

  <!-- Error State -->
  @if (hasError && !isLoading) {
    <div class="config-empty-state">
      <app-error-state></app-error-state>
    </div>
  }

  <!-- Configuration Cards -->
  @if (selectedAccountId && configuration && !hasError && !isLoading) {
    <!-- Notification Lead Time (full width) -->
    <div class="config-card config-card--full">
      <h3 class="config-card__title">{{ t('maintenance.notificationLeadTime') }}</h3>
      <div class="config-card__content">
        <div class="config-field">
          <label class="config-field__label">{{ t('maintenance.upcomingLeadTime') }}<span class="config-field__required">*</span></label>
          <span ngbDropdown class="config-dropdown" [autoClose]="true">
            <button type="button" class="btn form-control dropdown-toggle config-dropdown__trigger" id="leadTimeDropdown" ngbDropdownToggle>
              <div class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ leadTimeLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>
            <div ngbDropdownMenu aria-labelledby="leadTimeDropdown">
              <div class="d-flex flex-column">
                @for (option of leadTimeOptions; track option.value) {
                  <button class="btn btn-outline" (click)="onLeadTimeChange(option.value)" ngbDropdownItem>{{ option.label }}</button>
                }
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>

    <!-- Bottom Row: 2-column grid -->
    <div class="config-grid">
      <!-- Maintenance Request Auto-Approval -->
      <div class="config-card">
        <h3 class="config-card__title">{{ t('maintenance.autoApproval.title') }}</h3>
        <div class="config-card__content">
          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.autoApproval.oem.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.autoApproval.oem.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveOemRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.autoApproval.driver.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.autoApproval.driver.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveDriverRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.autoApproval.workshop.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.autoApproval.workshop.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveWorkshopRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.autoApproval.compliance.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.autoApproval.compliance.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveComplianceRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.autoApproval.canBus.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.autoApproval.canBus.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveCanBusRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <!-- Technician Capacity & Work Orders -->
      <div class="config-card">
        <h3 class="config-card__title">{{ t('maintenance.capacity.title') }}</h3>
        <div class="config-card__content">
          <div class="config-field">
            <label class="config-field__label">{{ t('maintenance.capacity.maxJobs') }}<span class="config-field__required">*</span></label>
            <span ngbDropdown class="config-dropdown" [autoClose]="true">
              <button type="button" class="btn form-control dropdown-toggle config-dropdown__trigger" id="maxJobsDropdown" ngbDropdownToggle>
                <div class="d-flex flex-row justify-content-between dropdown-inner">
                  <span class="align-middle">{{ configuration.maxJobsPerDay > 1 ? t('maintenance.capacity.jobPlural', { count: configuration.maxJobsPerDay }) : t('maintenance.capacity.jobSingular', { count: configuration.maxJobsPerDay }) }}</span>
                  <i class="ph ph-caret-down lh-base"></i>
                </div>
              </button>
              <div ngbDropdownMenu aria-labelledby="maxJobsDropdown">
                <div class="d-flex flex-column config-dropdown__scrollable">
                  @for (option of jobsPerDayOptions; track option) {
                    <button class="btn btn-outline" (click)="onMaxJobsChange(option)" ngbDropdownItem>{{ option > 1 ? t('maintenance.capacity.jobPlural', { count: option }) : t('maintenance.capacity.jobSingular', { count: option }) }}</button>
                  }
                </div>
              </div>
            </span>
          </div>

          <div class="config-field">
            <label class="config-field__label">{{ t('maintenance.capacity.maxHours') }}<span class="config-field__required">*</span></label>
            <span ngbDropdown class="config-dropdown" [autoClose]="true">
              <button type="button" class="btn form-control dropdown-toggle config-dropdown__trigger" id="maxHoursDropdown" ngbDropdownToggle>
                <div class="d-flex flex-row justify-content-between dropdown-inner">
                  <span class="align-middle">{{ configuration.maxHoursPerDay > 1 ? t('maintenance.capacity.hourPlural', { count: configuration.maxHoursPerDay }) : t('maintenance.capacity.hourSingular', { count: configuration.maxHoursPerDay }) }}</span>
                  <i class="ph ph-caret-down lh-base"></i>
                </div>
              </button>
              <div ngbDropdownMenu aria-labelledby="maxHoursDropdown">
                <div class="d-flex flex-column config-dropdown__scrollable">
                  @for (option of hoursPerDayOptions; track option) {
                    <button class="btn btn-outline" (click)="onMaxHoursChange(option)" ngbDropdownItem>{{ option > 1 ? t('maintenance.capacity.hourPlural', { count: option }) : t('maintenance.capacity.hourSingular', { count: option }) }}</button>
                  }
                </div>
              </div>
            </span>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.capacity.autoAssign.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.capacity.autoAssign.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoAssignTechnician" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="config-toggle">
            <div class="config-toggle__info">
              <span class="config-toggle__label">{{ t('maintenance.capacity.autoApproveWorkOrder.label') }}</span>
              <span class="config-toggle__description">{{ t('maintenance.capacity.autoApproveWorkOrder.helper') }}</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" [(ngModel)]="configuration.autoApproveWorkOrderRequest" (change)="onToggleChange()">
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  }
</div>
